/**
 * Instructors Resource Page
 * Manage flight instructors
 */

import React from 'react';
import { Typography, Button, Space, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useResources,
  useToggleResourceAvailability,
} from '@/hooks/useCalendars';
import { ResourceTable } from '@/components/features/goldenwings/ResourceTable';
import type { Resource } from '@/api/types/calendars.types';

const { Title } = Typography;

export const InstructorsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: resources = [], isLoading } = useResources({
    resource_type: 'INSTRUCTOR',
  });
  const toggleAvailability = useToggleResourceAvailability();

  const handleEdit = (resource: Resource) => {
    message.info(`Edit instructor: ${resource.name}`);
  };

  const handleDelete = (resource: Resource) => {
    message.info(`Delete instructor: ${resource.name}`);
  };

  const handleToggleAvailability = async (resource: Resource) => {
    try {
      await toggleAvailability.mutateAsync({
        resourceId: resource.id,
        data: {
          is_available: !resource.is_available,
          availability_notes: !resource.is_available
            ? 'Made available'
            : 'Made unavailable',
        },
      });
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleCreate = () => {
    message.info('Create new instructor');
  };

  return (
    <div style={{ padding: 24 }}>
      <Space
        direction="vertical"
        size="large"
        style={{ width: '100%', marginBottom: 24 }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            {t('goldenwings.instructors')}
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add Instructor
          </Button>
        </div>

        <ResourceTable
          resourceType="INSTRUCTOR"
          resources={resources}
          loading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleAvailability={handleToggleAvailability}
        />
      </Space>
    </div>
  );
};
